import React from 'react';

interface HaccpSectionHeaderProps {
  /** Section display label (e.g., "February 2026" or "Trial Period") */
  label: string;
  /** Number of items in the section */
  itemCount: number;
  /** Whether the section is currently expanded */
  isExpanded: boolean;
  /** Whether this is the trial section (uses amber styling) */
  isTrialSection: boolean;
  /** Callback when the header is clicked to toggle expansion */
  onToggle: () => void;
}

export const HaccpSectionHeader: React.FC<HaccpSectionHeaderProps> = ({
  label,
  itemCount,
  isExpanded,
  isTrialSection,
  onToggle
}) => {
  // Styling based on section type
  const backgroundColor = isTrialSection ? '#fef3c7' : '#f3f4f6'; // Amber-100 for trial, gray-100 for monthly
  const borderColor = isTrialSection ? '#fcd34d' : '#d1d5db'; // Amber-300 for trial, gray-300 for monthly
  const textColor = isTrialSection ? '#92400e' : '#374151'; // Amber-800 for trial, gray-700 for monthly
  const hoverBg = isTrialSection ? '#fde68a' : '#e5e7eb'; // Amber-200 for trial, gray-200 for monthly

  return (
    <button
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '12px 16px',
        backgroundColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        cursor: 'pointer',
        marginBottom: isExpanded ? '12px' : '8px',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = hoverBg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = backgroundColor;
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Chevron indicator */}
        <span
          style={{
            display: 'inline-block',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            fontSize: '12px',
            color: textColor
          }}
        >
          ▶
        </span>

        {/* Section label */}
        <span
          style={{
            fontWeight: 600,
            fontSize: '14px',
            color: textColor,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          {label}
        </span>

        {/* Item count badge */}
        <span
          style={{
            backgroundColor: isTrialSection ? '#f59e0b' : '#6b7280', // Amber-500 for trial, gray-500 for monthly
            color: 'white',
            fontSize: '12px',
            fontWeight: 500,
            padding: '2px 8px',
            borderRadius: '10px',
            minWidth: '24px',
            textAlign: 'center'
          }}
        >
          {itemCount}
        </span>
      </div>

      {/* Trial period note */}
      {isTrialSection && (
        <span
          style={{
            fontSize: '12px',
            color: '#b45309', // Amber-700
            fontStyle: 'italic'
          }}
        >
          Data from testing period
        </span>
      )}
    </button>
  );
};
